/** @format */

/* eslint require-await: "warn" */
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TFunction } from 'i18next';

import { AvailableMarket, Market, MarketCode } from '@toucan/config/markets';

import { SubmissionErrorCode } from '@toucan/types';
import {
    CustomerPhoneNumber,
    CustomerPhoneNumberResponse,
} from '@toucan/types/customer';
import { Model } from '@toucan/types/model';
import { SavedProduct } from '@toucan/types/product';

import { FormValues, client } from '@toucan/services/request-client';

import { transformSavedProduct } from '@toucan/helpers/products';

import { PersonalDetailsFormValues } from '@toucan/components/views/personal-details/components/personal-details-form';

import {
    transformInboundAddressData,
    transformOutboundAddressData,
} from '@toucan/client/core/address/helpers/address';
import { Address, AddressWithId } from '@toucan/client/global/transaction';
import { User } from '@toucan/common/services/user/types';

/**
 * Call the sign up API with the given form values
 *
 * @param values {FormValues} the user details to sign up with
 * @param market {MarketCode} the market code
 */
export async function signUp(
    values: FormValues,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<{ providedEmail: string }>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    return client.post('/public-api/v3/user/signup/', values, config);
}

/**
 * Quick sign up still uses the confirm password field.
 * This is likely to be removed in the future due to balance work.
 */
export async function quickSignUp(
    values: FormValues,
    market: MarketCode,
    csrfToken: string
): Promise<AxiosResponse<{ providedEmail: string }>> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    return client.post('/public-api/v2/user/signup/', values, config);
}

/**
 * Request re-sending the account verification email
 *
 * @param email {string} the email to re-send the verification to
 * @param market {MarketCode} the market code
 */
export async function sendVerification(
    email: string,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        '/public-api/v1/user/email-address/send-verification/',
        {
            emailAddress: email,
        },
        config
    );
}

/**
 * Verify a user account using the account verification hash
 *
 * @param hash {string} the account verification hash
 * @param market {MarketCode} the market code
 */
export async function verify(hash: string, market: MarketCode): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `AuthLink ${hash}`,
            'X-Authlink-Action': 'email_verification',
            'X-Market': market.toLowerCase(),
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    };

    return client.post('/public-api/v2/hashed/user/verify-email/', {}, config);
}

/**
 * Separate the email verification hash from the current
 * window location path and return them as a tuple
 */
export function getEmailVerifyPathAndHash(): [string, string] {
    const pathArray = window.location.pathname.split('/');
    const hash = pathArray[4];
    pathArray.splice(pathArray.length - 1, 1);

    return [pathArray.join('/'), hash];
}

/**
 * Get a new anonymous JWT token.
 *
 * The token can be used with the basket API if we don't have a logged in token
 * @param market {MarketCode} the market code
 */
export async function getAnonymousToken(market: MarketCode): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post('/public-api/v2/jwt/new/', {}, config);
}

/**
 * Upgrades a token to a JWT
 * @param token A token to upgrade to a jwt
 * @param market {MarketCode} the market code
 */
export function upgradeAuthLinkToJWT(
    token: string,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `AuthLink ${token}`,
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post('/public-api/v2/jwt/upgrade/', {}, config);
}

/**
 * Attempt to log in the user.
 *
 * @param values {FormValues} the login form field values
 * @param values.email {string} the account email
 * @param values.password {string} the account password
 * @param values.rememberMe {boolean} whether to 'remember' the user and store a long life cookie
 * @param market {MarketCode} the market code
 */
export async function login(
    values: FormValues,
    market: MarketCode,
    csrfToken: string
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
        timeout: 60000,
    };

    return client.post('/public-api/v2/user/login/', values, config);
}

/**
 * Logs the user out of their account
 */
export function logout() {
    return client.post('/logout');
}

/**
 * Get the logged in user details. Auth is handled by jwt stored as a http cookie.
 *
 * @param market {MarketCode} the market code
 */
export const getUser = async (market: MarketCode) => {
    const { data, ...response } = await client.get<User>(
        '/public-api/v2/user/me/',
        {
            headers: {
                'X-Market': market.toLowerCase(),
            },
        }
    );

    return {
        ...response,
        data: {
            ...data,
            // Replace null phone number with empty string
            primaryPhoneNumber: data.primaryPhoneNumber ?? '',
        },
    };
};

/**
 * Gets the password reset hash from the current window location path
 *
 * @returns {string} The password reset hash
 */
export function getPasswordResetHash(): string {
    const pathArray = window.location.pathname.split('/');
    return pathArray[4];
}

/**
 * Check whether the password reset hash is valid.
 *
 * @param hash {string} the hash to check
 * @param market {MarketCode} the market code
 * @returns {string} The password reset hash
 */
export function isPasswordResetHashValid(
    hash: string,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `AuthLink ${hash}`,
            'X-Authlink-Action': 'reset_password',
            'X-Market': market.toLowerCase(),
            'Content-type': 'application/x-www-form-urlencoded',
        },
    };

    return client.options('/public-api/v2/hashed/user/reset-password/', config);
}

/**
 * Resets the users password
 *
 * @param hash {String} the Hash to authenticate with
 * @param password {String} The new password
 * @param confirmPassword {String} A confirmation of the new password
 * @param market {MarketCode} the market code
 */
export async function resetPassword(
    hash: string,
    password: string,
    confirmPassword: string,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            Authorization: `AuthLink ${hash}`,
            'X-Authlink-Action': 'reset_password',
            'X-Market': market.toLowerCase(),
            'Content-type': 'application/x-www-form-urlencoded',
        },
    };

    return client.post(
        '/public-api/v2/hashed/user/reset-password/',
        {
            password,
            confirm: confirmPassword,
        },
        config
    );
}

/**
 * Request a password reset for the account with the given email address.
 *
 * @param email {string} the email address attached to the account
 * @param market {MarketCode} the market code
 */
export async function requestPasswordReset(
    email: string,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        '/public-api/v1/user/password-reset/request/',
        {
            email,
        },
        config
    );
}

/**
 * Gets the address book for the currently logged in user
 *
 * @param market {MarketCode} the market code
 */
export async function getAddressBook(
    market: MarketCode
): Promise<AddressWithId[]> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };
    const response = await client.get(
        '/public-api/v2/user/address-book/',
        config
    );

    return response.data.map(transformInboundAddressData);
}

/**
 * Add an address to the currently logged in user
 *
 * @param address the address to add
 * @param market {MarketCode} the market code
 * @returns the address id
 */
export async function addAddress(
    address: Address,
    market: MarketCode
): Promise<number> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.post(
        `/public-api/v2/user/add-address/`,
        transformOutboundAddressData(address),
        config
    );

    return response.data.addressId;
}

export async function updateAddress(
    address: AddressWithId,
    market: MarketCode
): Promise<AddressWithId> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.put(
        `/public-api/v1/user/address-book/${address.id}/`,
        transformOutboundAddressData(address),
        config
    );

    return response.data;
}

/**
 * Request to check if an account already exists for an email.
 * Will return true if an account with the email doesn't exist
 *
 * @param emailAddress {string} The email to check
 * @param market {MarketCode} the market code
 * @returns A promise that resolves to a boolean
 */
export async function verifyCustomerEmail(
    emailAddress: string,
    market: MarketCode
): Promise<boolean> {
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Market': market.toLowerCase(),
        },
    };

    try {
        const values: FormValues = {
            emailAddress,
        };

        await client.post('/public-api/user/check-email/', values, config);

        return true;
    } catch (error: any) {
        if (
            error?.response?.data?.errorCode === SubmissionErrorCode.DUPLICATE
        ) {
            return false;
        }

        throw error;
    }
}

/**
 * Gets the saved products for a user
 * @async
 * @returns {Product[]} An array of products saved by the user
 */
export async function getSavedProducts(
    market: Market,
    availableMarkets: AvailableMarket[],
    t: TFunction
): Promise<SavedProduct[]> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.code.toLowerCase(),
        },
    };
    const response = await client.get(
        '/public-api/v2/user/favourite/product/',
        config
    );

    return response.data.map(({ product }: { product: SavedProduct }) =>
        transformSavedProduct(product, market, availableMarkets, t)
    );
}

/**
 * Request to handle saving a product to the users saved products in their account.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id Product id
 * @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function addSavedProduct(
    market: Market,
    availableMarkets: AvailableMarket[],
    id: number,
    t: TFunction
): Promise<SavedProduct | undefined> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.code.toLowerCase(),
        },
    };

    const response = await client.post(
        `/public-api/v2/user/favourite/product/add/${id}/`,
        config
    );

    const product: SavedProduct = response?.data?.product;
    if (!product) {
        return;
    }

    return transformSavedProduct(product, market, availableMarkets, t);
}

/**
 * Request to handle removing a saved product.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id Product id
 * @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function removeSavedProduct(
    market: MarketCode,
    id: number
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        `/public-api/v2/user/favourite/product/delete/${id}/`,
        config
    );
}

export enum UpdateMarketingPreferenceChoice {
    EMAIL = 'receive_offers_via_email',
    TEXT = 'receive_offers_via_text',
}

/**
 * Updates a users marketing preference
 *
 * @param pref The marketing preference to update
 * @param value If the user has opted in to this choice
 */
export async function updateMarketingPreference(
    preference: UpdateMarketingPreferenceChoice,
    value: boolean
) {
    const data = { value };
    const config: AxiosRequestConfig = {
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    };

    return client.post(
        `/public-api/v1/user/set-preference/${preference}/`,
        data,
        config
    );
}

/**
 * Gets the saved models for a user
 * @async
 * @returns {Promise<Model[]>} An array of Models saved by the user
 */
export async function getSavedModels(market: Market): Promise<Model[]> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.code.toLowerCase(),
        },
    };
    const response = await client.get(
        '/public-api/v2/user/favourite/model/',
        config
    );

    return response.data.map(({ model }: any) => model);
}

/**
 * Request to handle saving a model to the users saved models in their account.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id {number} Model id
 * @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function addSavedModel(
    market: MarketCode,
    id: number
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        `/public-api/v2/user/favourite/model/add/${id}/`,
        config
    );
}

/**
 * Request to handle removing a saved model.
 *
 * @async
 * @param market {MarketCode} the market code
 * @param id {number} Model id
 * @returns @returns {AxiosResponse} A promise that resolves with a response from the api
 */
export async function removeSavedModel(
    market: MarketCode,
    id: number
): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.post(
        `/public-api/v2/user/favourite/model/delete/${id}/`,
        config
    );
}

export async function updatePersonalInformation(
    customerId: number,
    personalDetails: PersonalDetailsFormValues,
    market: MarketCode
): Promise<void> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    return client.put(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/`,
        personalDetails,
        config
    );
}

/**
 * Request to update the users' password
 *
 * @param password {string} Current users password
 * @param newPassword {string} The new password to be updated to
 * @param customerId {string} Id of the customer
 * @param market {MarketCode} the market code
 */
export function updatePassword(
    password: string,
    newPassword: string,
    customerId: number,
    market: MarketCode
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'Content-Type': 'application/x-www-form-urlencoded',
        },
    };

    const data = {
        password,
        newPassword,
    };

    // TODO: Update endpoint with trailing slash when updated
    return client.post(
        `/public-api/customer-service/v1/customer/${customerId}/authentication/password/`,
        data,
        config
    );
}

/**
 * Call out to GeoLocate API
 */
export function geolocateUser(): Promise<
    AxiosResponse<{ userMarket: string }>
> {
    return client.get('/geolocate');
}

/**
 * Gets a list of phone numbers for the given customerId
 * This will default to 50 phone numbers, we will not display more than
 * 50 numbers for MVP, so we will need to handle this postMVP
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function getUserPhoneNumbers(
    customerId: number,
    market: MarketCode
): Promise<CustomerPhoneNumberResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };
    const response = await client.get(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/`,
        config
    );

    return response.data;
}

/**
 * Deletes a phone number for the given customerId
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function deletePhoneNumber(
    customerId: number,
    phoneNumberId: number,
    market: MarketCode
): Promise<boolean> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.delete(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/${phoneNumberId}/`,
        config
    );

    return response.data;
}

/**
 * Creates a phone number for the given customerId
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function createPhoneNumber(
    customerId: number,
    phoneNumber: string,
    market: MarketCode
): Promise<CustomerPhoneNumberResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.post(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/`,
        { phoneNumber },
        config
    );

    return response.data;
}

/**
 * Updates a phone number for the given customerId
 *
 * @param market {MarketCode} the market code
 * @param market {customerId} the customerId
 */
export async function updatePhoneNumber(
    customerId: number,
    { phoneNumber, id }: CustomerPhoneNumber,
    market: MarketCode
): Promise<CustomerPhoneNumberResponse> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.put(
        `/public-api/customer-service/v1/customer/${customerId}/personal-information/phone-numbers/${id}/`,
        { phoneNumber },
        config
    );

    return response.data;
}
